import { useEffect, useState } from 'react';
import { initializePaddle } from '@paddle/paddle-js';

// Utils
import useUser from '@/utils/hooks/swr/useUser';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';

// Types
import type { Environments, Paddle } from '@paddle/paddle-js';
import type { CheckoutLinkDataType } from '@/utils/hooks/swr/pricing/useCheckoutLink';
import { ActivePlanType } from '@/types/pricingTypes/subscriptionType';

type IframeCheckoutChangePaymentProps = {
    customerId: CheckoutLinkDataType['customer_id'];
    isIframeLoaded: boolean;
    onSuccess: () => void;
    setIsIframeLoaded: React.Dispatch<React.SetStateAction<boolean>>;
};

const PADDLE_CONTAINER = 'inline-checkout-container';

export function IframeCheckoutChangePayment({
    customerId,
    isIframeLoaded,
    setIsIframeLoaded,
    onSuccess
}: IframeCheckoutChangePaymentProps) {
    const { user } = useUser();
    const { subscription } = useSubscription();
    const [paddleInstance, setPaddleInstance] = useState<Paddle>(null);
    const currentSubscription = subscription as ActivePlanType;

    useEffect(() => {
        initializePaddle({
            environment: process.env.NEXT_PUBLIC_PADDLE_ENVIRONMENT as Environments,
            token: process.env.NEXT_PUBLIC_PADDLE_TOKEN,
            checkout: {
                settings: {
                    theme: user.settings.theme
                }
            },
            eventCallback: async (event) => {
                if (event.name === 'checkout.loaded') {
                    setIsIframeLoaded(true);
                }

                if (event.name === 'checkout.completed') {
                    onSuccess();
                }
            }
        }).then((paddle: Paddle) => {
            if (paddle) {
                setPaddleInstance(paddle);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isIframeLoaded && paddleInstance) {
            paddleInstance.Checkout.open({
                customer: {
                    id: customerId
                },
                transactionId: currentSubscription.transaction_id,
                settings: {
                    displayMode: 'inline',
                    frameTarget: PADDLE_CONTAINER,
                    frameInitialHeight: 380,
                    frameStyle: 'width:100%; background-color: transparent;',
                    allowDiscountRemoval: false
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIframeLoaded, paddleInstance]);

    return <div className={PADDLE_CONTAINER} />;
}
